/**
 * Breadcrumb items hook
 *
 * This hook manages the generation and resolution of breadcrumb navigation items.
 * It handles entity name resolution via API calls and builds the folder hierarchy.
 */

import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import type { FolderWithCounts, WorkspaceItemType } from '@loader/shared-types';

import {
  UUID_PATTERN,
  getEntityType,
  formatSegmentLabel,
  type BreadcrumbItemData,
  type BreadcrumbEntityType,
} from './breadcrumbUtils';

import { useRpc } from '@/lib/rpc';
import { useFoldersQuery, useFolderQuery, useTagQuery } from '@/features/organization/hooks';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks';
import type { Table, LinkedTable } from '@/lib/types';
import type { WorkspaceSearchParams } from '@/features/workspace/utils/workspaceSearchSchema';


/**
 * Resolve entity name from stores or API
 *
 * @param entityType - Type of entity to resolve
 * @param entityId - ID of the entity
 * @param stores - Local data stores
 * @param rpc - RPC client for API calls
 * @returns Resolved entity name or null
 */
async function resolveEntityName(
  entityType: BreadcrumbEntityType | null,
  entityId: string,
  stores: {
    tables: Table[];
    linkedTables: LinkedTable[];
    folders: FolderWithCounts[];
  },
  rpc: ReturnType<typeof useRpc>
): Promise<string | null> {
  if (!entityType || !UUID_PATTERN.test(entityId)) {
    return null;
  }

  // Try to find in stores first (fastest)
  if (entityType === 'table') {
    const table = stores.tables.find((t) => t.id === entityId);
    if (table) {
      return table.name;
    }
    // Fallback to API if not in store
    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id: entityId },
      });
      if (res.ok) {
        const data = await res.json();
        return data.table.name;
      }
    } catch (error) {
      console.warn('Failed to fetch table name:', error);
    }
  }

  if (entityType === 'linkedTable') {
    const linkedTable = stores.linkedTables.find((lt) => lt.id === entityId);
    if (linkedTable) {
      return linkedTable.name;
    }
    // Fallback to API if not in store
    try {
      // @ts-expect-error - Hono RPC has limitations with hyphenated routes
      const res = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id: entityId },
      });
      if (res.ok) {
        const data = await res.json();
        return data.linkedTable.name;
      }
    } catch (error) {
      console.warn('Failed to fetch linkedTable name:', error);
    }
  }

  if (entityType === 'folder') {
    const folder = stores.folders.find((f) => f.id === entityId);
    if (folder) {
      return folder.name;
    }
    // Fallback to API if not in store
    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders[':id'].$get({
        param: { id: entityId },
      });
      if (res.ok) {
        const data = await res.json();
        return (data as { folder: { name: string } }).folder.name;
      }
    } catch (error) {
      console.warn('Failed to fetch folder name:', error);
    }
  }

  // For versions and schemas, we'd need to fetch from API
  // For now, return null to show the UUID
  return null;
}

/**
 * Hook to generate and manage breadcrumb items
 *
 * @param items - Optional pre-defined breadcrumb items
 * @returns Object containing breadcrumb items and loading state
 */
export function useBreadcrumbItems(items?: BreadcrumbItemData[]) {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const searchParams = router.location.search as WorkspaceSearchParams;

  // Fetch data using TanStack Query hooks
  const { data: tables = [], isError: tablesError } = useTablesListQuery();
  const { data: linkedTables = [], isError: linkedTablesError } = useLinkedTablesListQuery();
  const { data: folders, isError: foldersError } = useFoldersQuery();
  const { data: tag } = useTagQuery(searchParams.tag);
  // Fetch single folder for trash navigation (GET /folders/:id returns trashed folders too)
  const { data: trashFolder } = useFolderQuery(searchParams.folder);

  const rpc = useRpc();
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItemData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Stable references for data arrays - use JSON.stringify to detect actual data changes
  const tablesKey = JSON.stringify(tables.map((t) => ({ id: t.id, name: t.name, folder_id: t.folder_id })));
  const linkedTablesKey = JSON.stringify(linkedTables.map((lt) => ({ id: lt.id, name: lt.name, folder_id: lt.folder_id })));
  const foldersKey = JSON.stringify(folders?.map((f) => ({ id: f.id, name: f.name, parent_folder_id: f.parent_folder_id })) ?? []);
  const tagKey = tag ? JSON.stringify({ id: tag.id, name: tag.name }) : null;
  // Generate breadcrumbs when path changes
  useEffect(() => {
    setIsLoading(true);

    // If items are provided, use them directly
    if (items) {
      setBreadcrumbItems(items);
      setIsLoading(false);
      return;
    }

    // Handle query errors - show generic breadcrumbs if data fetch failed
    if (tablesError || linkedTablesError || foldersError) {
      // Fallback to minimal breadcrumbs on error
      const segments = currentPath.split('/').filter(Boolean);
      const fallbackBreadcrumbs: BreadcrumbItemData[] = [
        {
          label: 'Home',
          href: '/workspace',
          isLoading: false,
          iconType: 'workspace',
        },
      ];

      // Add last segment as current page
      const lastSegment = segments[segments.length - 1];
      if (lastSegment && lastSegment !== 'workspace') {
        const label = formatSegmentLabel(lastSegment);
        fallbackBreadcrumbs.push({
          label,
          href: undefined,
        });
      }

      setBreadcrumbItems(fallbackBreadcrumbs);
      setIsLoading(false);
      return;
    }

    // Generate breadcrumbs: Workspace > [folder path] > [entity name]
    const segments = currentPath.split('/').filter(Boolean);
    const initialBreadcrumbs: BreadcrumbItemData[] = [];

    // Always start with Workspace as root
    initialBreadcrumbs.push({
      label: 'Home',
      href: '/workspace',
      isLoading: false,
      iconType: 'workspace',
    });

    // Check for special views (favorites, recent, trash)
    const viewType = segments.find(s => ['favorites', 'recent', 'trash'].includes(s));

    if (viewType) {

      if (viewType === 'trash') {
        // Trash is the root — replace "Workspace" with "Trash"
        initialBreadcrumbs[0] = {
          label: 'Trash',
          href: searchParams.folder ? '/workspace/trash' : undefined,
          isLoading: false,
          iconType: 'trash',
        };

        // If navigating inside a trashed folder, add folder name
        if (searchParams.folder) {
          initialBreadcrumbs.push({
            label: trashFolder?.name || 'Folder',
            href: undefined,
            isLoading: false,
            iconType: 'folder',
          });
        }

        setBreadcrumbItems(initialBreadcrumbs);
        setIsLoading(false);
        return;
      }

      // For other special views, just add the view as the current page
      if (viewType) {
        initialBreadcrumbs.push({
          label: formatSegmentLabel(viewType),
          href: undefined,
        });
      }
      setBreadcrumbItems(initialBreadcrumbs);
      setIsLoading(false);
      return;
    }

    // Check for tag filtering
    if (searchParams.tag && currentPath === '/workspace') {
      // Add "Tags" and the tag name
      initialBreadcrumbs.push({
        label: 'Tags',
        href: undefined,
      });
      initialBreadcrumbs.push({
        label: tag?.name || searchParams.tag,
        href: undefined,
        isLoading: !tag,
      });
      setBreadcrumbItems(initialBreadcrumbs);
      setIsLoading(false);
      return;
    }

    // Find entity ID and type from path
    let entityId: string | null = null;
    let entityType: 'table' | 'linkedTable' | 'folder' | null = null;

    segments.forEach((segment, index) => {
      if (UUID_PATTERN.test(segment)) {
        entityId = segment;
        const type = getEntityType(segments, index);
        // Only track entity types that we show in breadcrumbs
        if (type === 'table' || type === 'linkedTable' || type === 'folder') {
          entityType = type;
        }
      }
    });

    // Build folder hierarchy and entity breadcrumb
    if (entityId && entityType) {
      let resolvedName: string | null = null;
      let folderId: string | null | undefined = null;

      if (entityType === 'table') {
        const table = tables.find((t) => t.id === entityId);
        resolvedName = table?.name ?? null;
        folderId = table?.folder_id;
      } else if (entityType === 'linkedTable') {
        const linkedTable = linkedTables.find((lt) => lt.id === entityId);
        resolvedName = linkedTable?.name ?? null;
        folderId = linkedTable?.folder_id;
      } else if (entityType === 'folder') {
        const folder = folders?.find((f) => f.id === entityId);
        resolvedName = folder?.name ?? null;
        folderId = folder?.parent_folder_id;
      }

      // Build folder chain
      if (folderId && folders) {
        const folderChain: { id: string; name: string }[] = [];
        let currentFolder = folders.find((f) => f.id === folderId);

        while (currentFolder) {
          folderChain.unshift({ id: currentFolder.id, name: currentFolder.name });
          if (currentFolder.parent_folder_id) {
            currentFolder = folders.find((f) => f.id === currentFolder!.parent_folder_id);
          } else {
            break;
          }
        }

        // Add folder breadcrumbs
        folderChain.forEach((folder) => {
          initialBreadcrumbs.push({
            label: folder.name,
            href: `/workspace/folder/${folder.id}`,
            isLoading: false,
            iconType: 'folder',
          });
        });
      }

      // Map entityType to WorkspaceItemType for icon
      const iconType: WorkspaceItemType = entityType === 'linkedTable' ? 'linked_table' : entityType;

      // Add entity breadcrumb (last item, no href)
      initialBreadcrumbs.push({
        label: resolvedName || entityId,
        href: undefined,
        isLoading: !resolvedName,
        iconType,
      });

      // If name not found, resolve asynchronously
      if (!resolvedName && entityId) {
        const segmentId = entityId;
        const breadcrumbIndex = initialBreadcrumbs.length - 1;

        void resolveEntityName(
          entityType,
          entityId,
          { tables, linkedTables, folders: folders ?? [] },
          rpc
        ).then((name) => {
          setBreadcrumbItems((prev) => {
            if (prev[breadcrumbIndex]?.label === segmentId) {
              const updated = [...prev];
              updated[breadcrumbIndex] = {
                ...updated[breadcrumbIndex],
                label: name || segmentId,
                isLoading: false,
              };
              return updated;
            }
            return prev;
          });
        });
      }
    } else {
      // For non-entity pages (settings, etc.), show the route segment
      const lastSegment = segments[segments.length - 1];
      if (lastSegment && lastSegment !== 'workspace') {
        const label = formatSegmentLabel(lastSegment);
        initialBreadcrumbs.push({
          label,
          href: undefined,
        });
      }
    }

    setBreadcrumbItems(initialBreadcrumbs);
    setIsLoading(false);
    // Note: Error states (tablesError, linkedTablesError, foldersError) are intentionally
    // excluded from dependencies - we handle errors via the early return above and don't
    // need to re-run the effect when only the error state changes.
    // We use *Key variables (JSON strings) instead of the arrays themselves to prevent
    // infinite loops when array references change but data is the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, items, tablesKey, linkedTablesKey, foldersKey, tagKey, searchParams.tag, searchParams.folder, trashFolder?.name, rpc]);

  return { breadcrumbItems, isLoading };
}
